import * as React from 'react'
import Navbar from '../../setup/Navbar'
import {
    Container,
    Grid2,
    Typography,
    Box,
    TextField,
    Button,
    Autocomplete,
    Alert
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import HeadingText from '../../../BuiltInComponent/HeadingTest'
import ButtonNormal from '../../../BuiltInComponent/ButtonNormal'
import Loading from '../../../BuiltInComponent/Loading'
import StickyHeadTable from '../../../BuiltInComponent/TableWithPagination'
import { getItemList } from '../../../features/setup/useableSlice'
import {
    getAllAuditList,
    getAuditData,
    getSystemStock,
    saveOpeningStockEntry
} from '../../../features/AdminManagement/auditSlice'

export default function OpeningStockEntry() {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { user } = useSelector((state) => state.auth)
    const { items } = useSelector((state) => state.ru)
    const { listData, masterData, isLoading } = useSelector((state) => state.audit)

    const [selectedAuditID, setSelectedAuditID] = React.useState('')
    const [selectedItemCode, setSelectedItemCode] = React.useState('')
    const [openingStock, setOpeningStock] = React.useState(0)
    const [currentSystemStock, setCurrentSystemStock] = React.useState(0)
    const [message, setMessage] = React.useState('')

    React.useEffect(() => {
        dispatch(getAllAuditList({ getOrder: true }))
        dispatch(getItemList())
    }, [dispatch])

    React.useEffect(() => {
        if (selectedAuditID) {
            dispatch(getAuditData({ auditID: selectedAuditID }))
        }
    }, [dispatch, selectedAuditID])

    React.useEffect(() => {
        if (selectedItemCode) {
            dispatch(getSystemStock({ itemCode: selectedItemCode, branchID: user?.branchID || 1 }))
                .then((res) => {
                    const stock = res?.payload?.data?.[0]?.currentStock || 0
                    setCurrentSystemStock(Number(stock) || 0)
                })
        } else {
            setCurrentSystemStock(0)
        }
    }, [dispatch, selectedItemCode, user?.branchID])

    const onSaveOpeningStock = () => {
        if (!selectedAuditID) {
            setMessage('Please select an audit first')
            return
        }
        if (!selectedItemCode) {
            setMessage('Please select an item')
            return
        }

        dispatch(saveOpeningStockEntry({
            auditID: selectedAuditID,
            itemCode: selectedItemCode,
            openingStock: Number(openingStock) || 0,
            branchID: user?.branchID || 1,
            createdBy: user?.userID
        })).then((res) => {
            if (res?.payload?.status) {
                setMessage('Opening stock saved successfully')
                dispatch(getAuditData({ auditID: selectedAuditID }))
            } else {
                setMessage('Unable to save opening stock')
            }
        })
    }

    const tableColumns = [
        { fieldName: 'RONO', cName: 'Item Code', textAlign: 'left', width: 80, status: 'en', dType: 'string' },
        { fieldName: 'itemDesc', cName: 'Description', textAlign: 'left', width: 220, status: 'en', dType: 'string' },
        { fieldName: 'openingStock', cName: 'Opening Qty', textAlign: 'right', width: 120, status: 'en', dType: 'number' },
        { fieldName: 'currentSystemStock', cName: 'System Qty', textAlign: 'right', width: 120, status: 'en', dType: 'number' },
        { fieldName: 'quantity', cName: 'Audited Qty', textAlign: 'right', width: 120, status: 'en', dType: 'number' },
        { fieldName: 'variance', cName: 'Variance', textAlign: 'right', width: 100, status: 'en', dType: 'number' },
        { fieldName: 'Edit', cName: 'Edit', textAlign: 'center', width: 50, status: 'dis', dType: 'string' },
        { fieldName: 'Delete', cName: 'Delete', textAlign: 'center', width: 50, status: 'dis', dType: 'string' },
        { fieldName: 'Button', cName: '', textAlign: 'center', width: 50, status: 'dis', dType: 'string' }
    ]

    return (
        <>
            <Navbar />
            <Container>
                <Grid2 container spacing={2}>
                    <Grid2 size={12}>
                        <KeyboardBackspaceIcon
                            sx={{
                                fontSize: 30,
                                marginLeft: -1,
                                color: 'white',
                                cursor: 'pointer',
                                backgroundColor: '#1D4988',
                                padding: 1,
                                borderRadius: 50
                            }}
                            onClick={() => { navigate('/AuditListing') }}
                        />
                    </Grid2>

                    <Grid2 size={12} sx={{ marginTop: 3 }}>
                        <HeadingText text={'Opening Stock Entry'} />
                    </Grid2>

                    {message && (
                        <Grid2 size={12}>
                            <Alert severity={message.includes('successfully') ? 'success' : 'warning'}>{message}</Alert>
                        </Grid2>
                    )}

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                            options={listData?.data || []}
                            getOptionLabel={(option) => `Audit #${option.auditID} - ${option.remarks || ''}`}
                            value={(listData?.data || []).find((a) => a.auditID === selectedAuditID) || null}
                            onChange={(event, newValue) => {
                                setSelectedAuditID(newValue?.auditID || '')
                            }}
                            renderInput={(params) => <TextField {...params} label='Select Audit' size='small' />}
                            isOptionEqualToValue={(option, value) => option?.auditID === value?.auditID}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                            options={items || []}
                            getOptionLabel={(option) => option?.itemDesc || ''}
                            value={(items || []).find((i) => i.itemCode === selectedItemCode) || null}
                            onChange={(event, newValue) => {
                                setSelectedItemCode(newValue?.itemCode || '')
                            }}
                            renderInput={(params) => <TextField {...params} label='Select Item' size='small' />}
                            isOptionEqualToValue={(option, value) => option?.itemCode === value?.itemCode}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 2 }}>
                        <TextField
                            label='Opening Qty'
                            size='small'
                            fullWidth
                            type='number'
                            value={openingStock}
                            onChange={(e) => setOpeningStock(e.target.value)}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 2 }}>
                        <TextField
                            label='Current System Qty'
                            size='small'
                            fullWidth
                            value={currentSystemStock}
                            disabled
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 2 }}>
                        <ButtonNormal onClick={onSaveOpeningStock} text={'SAVE OPENING'} />
                    </Grid2>

                    <Grid2 size={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <Button
                                variant='outlined'
                                onClick={() => navigate('/InvAudit', { state: { auditID: selectedAuditID } })}
                                disabled={!selectedAuditID}
                            >
                                Go To Audit Screen
                            </Button>
                        </Box>
                    </Grid2>

                    <Grid2 size={12}>
                        {isLoading ? (
                            <Loading />
                        ) : (
                            <StickyHeadTable
                                tableColumns={tableColumns}
                                tableData={masterData?.data && masterData.data.length > 0 ? masterData.data : []}
                                perPage={25}
                            />
                        )}
                    </Grid2>
                </Grid2>
            </Container>
        </>
    )
}
